import React, { ChangeEvent, DragEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { CloudUploadOutlined } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';

import {
  gray10,
  gray35,
  gray100,
  gray80,
  gray110,
  grayBlue50,
  orange30,
  orange50,
  red50,
  white,
} from '../../../constants/colors';
import { formLabelWrapper, regularXs } from '../../../constants/location';

type Props = {
  helperText: string | undefined;
  onFilesSelected: (files: FileList | null) => void;
  onButtonClick?: () => boolean;
  multiple?: boolean;
  labelText?: string;
  requiredText?: string;
  labelFontSize?: string;
  height?: string;
  fileCountOverText?: string;
  disabled?: boolean;
  /** 選択できる拡張子（例: '.xlsx'）。未指定なら制限しない */
  accept?: string;
};

const PatternFileUploader = ({
  helperText,
  onFilesSelected,
  onButtonClick,
  labelText,
  requiredText,
  labelFontSize,
  multiple = true,
  height,
  fileCountOverText,
  disabled = false,
  accept,
}: Props): ReactElement => {
  const [borderColor, setBorderColor] = useState<string>(gray35);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  if (helperText == undefined && fileCountOverText !== '') {
    helperText = fileCountOverText;
  }
  const styles = {
    button: {
      height: '44px',
      padding: '16px',
      '& .MuiButton-icon': {
        marginRight: '4px',
      },
    },
    errorText: {
      ...regularXs,
      color: red50,
      marginTop: '4px',
    },
    icon24: {
      height: '24px',
      marginLeft: '0px',
      width: '24px',
    },
    input: {
      display: 'none',
    },
    dragText: {
      fontSize: '16px',
      fontWeight: 400,
      color: gray110,
      textAlign: 'center',
    },
    orText: {
      fontSize: '14px',
      fontWeight: 400,
      color: grayBlue50,
      textAlign: 'center',
    },
    uploadArea: {
      alignItems: 'center',
      justifyContent: 'center',
      border: `1px dashed ${gray35}`,
      borderRadius: '10px',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      padding: '24px 0',
      width: '100%',
      height: height ? height : 'auto',
      backgroundColor: gray10,
    },
    wrapper: {},
    formLabelWrapper,
    label: {
      fontSize: labelFontSize ? labelFontSize : '14px',
    },
    required: {
      color: red50,
      fontSize: labelFontSize ? labelFontSize : '14px',
      marginLeft: '6px',
    },
  };

  useEffect(() => {
    if (helperText) {
      setBorderColor(red50);
    } else {
      setBorderColor(gray35);
    }
  }, [helperText]);

  const handleButtonClick = (): void => {
    if (disabled) return;

    if (onButtonClick && !onButtonClick()) {
      return;
    }

    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
    if (disabled) return;
    const files = event.target.files;
    onFilesSelected(files);
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    if (disabled) return;
    const files = event.dataTransfer.files;
    onFilesSelected(files);
  };

  const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    if (disabled) return;
    setBorderColor(gray100);
  };

  return (
    <Box sx={styles.wrapper}>
      {labelText && (
        <Typography align="left" sx={styles.formLabelWrapper} component="div">
          <Typography color={gray80} sx={styles.label} component="span">
            {labelText}
          </Typography>

          {requiredText && (
            <Typography
              component="span"
              sx={{
                ...styles.required,
                textAlign: 'left',
                display: 'inline-block',
              }}
            >
              {requiredText}
            </Typography>
          )}
        </Typography>
      )}
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={(): void => setBorderColor(gray35)}
        sx={[styles.uploadArea, { borderColor: borderColor }]}
      >
        <input
          multiple={multiple}
          onChange={handleFileChange}
          ref={fileInputRef}
          style={styles.input}
          type="file"
          accept={accept}
        />
        <Typography sx={styles.dragText}>ファイルをここにドラッグ＆ドロップ</Typography>
        <Typography sx={styles.orText}>または</Typography>
        <Button
          onClick={handleButtonClick}
          disabled={disabled}
          startIcon={<CloudUploadOutlined sx={{ ...styles.icon24, color: orange50 }} />}
          sx={{
            ...styles.button,
            border: `1px solid ${orange50}`,
            borderRadius: '6px',
            backgroundColor: white,
            color: orange50,
            boxShadow: 'none',
            '&:hover': {
              backgroundColor: white,
              border: `1px solid ${orange30}`,
              boxShadow: 'none',
              opacity: '80%',
              '& .MuiTypography-root': {
                color: orange30,
              },
              '& .MuiSvgIcon-root': {
                color: orange30,
              },
            },
          }}
        >
          <Typography sx={{ fontSize: '16px', color: orange50 }}>ファイルを選択する</Typography>
        </Button>
      </Box>
      {helperText && (
        <Typography sx={{ ...styles.errorText, textAlign: 'left' }}>{helperText}</Typography>
      )}
    </Box>
  );
};

export default PatternFileUploader;
